// THE STORED ROW — one `byok_keys` record, envelope-encrypted at rest.
// Split out of `../byok.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { AccountIdSchema, StoredBYOKKeyIdSchema } from '../../value-objects/ids';
import { AllowedModelsSchema, BYOKKeyModelSchema } from './models';
import {
  BYOKKeyKindSchema,
  BYOKProviderSchema,
  BYOKStatusSchema,
  BYOKTierSchema,
} from './vocabulary';

/**
 * The WRITE range of `byok_keys.priority` (plan 102 slice 05b) — the pool's try-order,
 * lower first. One spelling for every door that sets it: the REST body
 * (`PATCH /v1/billing/keys/:id/priority` + the `/v1/me` mirror), capture's optional
 * field, and the MCP `key_priority_set` actions. `@developerz/db`'s write re-checks the
 * same bounds before its statement runs. The column itself is a plain integer, so a
 * row stored before this range existed still READS through {@link BYOKKeySchema}.
 */
export const BYOK_KEY_PRIORITY_MIN = 0;
export const BYOK_KEY_PRIORITY_MAX = 10_000;
/**
 * What an unset priority ranks as — the column default (`byok_keys.priority`
 * `DEFAULT 100`), the resolver's fallback for a hand-built key, and what the wire
 * projects for a row read without one.
 */
export const BYOK_KEY_PRIORITY_DEFAULT = 100;
export const BYOKKeyPrioritySchema = z
  .number()
  .int()
  .min(BYOK_KEY_PRIORITY_MIN)
  .max(BYOK_KEY_PRIORITY_MAX);

export const BYOKKeySchema = z.object({
  id: StoredBYOKKeyIdSchema,
  accountId: AccountIdSchema,
  /**
   * Owning USER within the account (the per-user provider cascade, epic #635).
   * `null` = an ORG key (the account's shared pool, owner/admin-managed). Set = a
   * member's PERSONAL key, usable only for work that member dispatches — it ranks
   * AHEAD of the org pool (`resolveLlmFromPool` acting-user partition), so a member
   * who brings their own provider signs their own work with it while the org pool
   * remains the fallback. Nullish for backward compatibility; DB rows normalize to
   * `null` (every pre-cascade key is an org key).
   */
  userId: z.string().nullish(),
  provider: BYOKProviderSchema,
  /** Provider-specific model id, e.g. `claude-opus-4-8`. */
  model: z.string().min(1),
  /**
   * Donor's model ceiling on a DONATED key (arc 101 slice 03) — see
   * {@link AllowedModelsSchema}. The recipient's pick lives on the DONATION
   * (`donations.chosenModel`), never here: the donor owns the key, the maintainer
   * owns the choice. Nullish like {@link baseUrl} and the pool fields below, so a
   * legacy row / hand-built key with no allowlist resolves exactly as before —
   * `null`/absent both mean "exactly {@link model}", never "anything".
   */
  allowedModels: AllowedModelsSchema.nullish(),
  /**
   * Endpoint override. Required for `custom` (an OpenAI-compatible endpoint has
   * no default); optional for `zai` (e.g. the coding-plan endpoint
   * `https://api.z.ai/api/coding/paas/v4`); optional for `kimi` (the platform
   * host `https://api.moonshot.ai/v1` is used when absent, while a supplied
   * `baseUrl` points at an alternate host such as the Kimi Code endpoint,
   * `https://api.kimi.com/coding/v1`); ignored elsewhere. Optional in the
   * schema for backward compatibility — DB rows normalize to `null`.
   */
  baseUrl: z.string().min(1).nullish(),
  /** Envelope-encrypted ciphertext. Domain layer never touches the plaintext. */
  encryptedKey: z.instanceof(Uint8Array),
  status: BYOKStatusSchema,
  // ── pool semantics (arc 101 slice 04) ──────────────────────────────────────
  // An account holds MANY keys at once — several per provider. These fields turn
  // the flat set into a routable pool; the assigner picks one per task
  // (`resolveLlmFromPool`) and ships it sealed. All optional (like `baseUrl`) so a
  // legacy row / hand-built key with no pool metadata resolves exactly as before:
  // the resolver applies the runtime defaults (`priority` 100, `active` true,
  // untagged). DB rows always carry concrete values (NOT NULL column defaults).
  /**
   * Account-unique handle a caller can pin with the envelope's `llm.key_label`
   * (explicit override). Null/absent when the maintainer never named the key.
   */
  label: z.string().min(1).max(64).nullish(),
  /** Pool try-order — lower is tried first within a match tier (resolver default 100). */
  priority: z.number().int().optional(),
  /** Capability tag driving tier routing; null/absent = untagged (matches any tier need). */
  tier: BYOKTierSchema.nullish(),
  /**
   * Pool on/off. Distinct from {@link status}: `active=false` parks a *valid* key
   * (the maintainer benched it) so the resolver skips it without a revoke. Absent
   * = active (the resolver default).
   */
  active: z.boolean().optional(),
  /**
   * OSS earmark (arc 101 slice 10). `'oss'` = a DONATED key: `resolveLlmFromPool`
   * only picks it for work on a PUBLIC repo (`ossEligible: true` — the claimed
   * task's repo, slice 02), so donated inference never signs private work.
   * Null/absent = an ordinary key (no restriction). Any non-null value is treated
   * as OSS-restricted (fail-safe).
   */
  earmark: z.string().min(1).nullish(),
  /**
   * LANE FENCE (#1460). Non-null = `resolveLlmFromPool` picks this key ONLY for
   * work whose lane matches it, and drops it for every other lane. Null/absent = a
   * general key usable by any lane — the pre-#1460 behaviour, so legacy rows and
   * hand-built keys resolve exactly as before.
   *
   * Deliberately a bare string, exactly like {@link earmark}, rather than an enum,
   * because validating HERE would make one bad stored row throw on READ and take the
   * whole pool down with it. The vocabulary (`LLM_ROUTE_PURPOSES` minus `default`)
   * is enforced on READ instead, on both sides, by `isLaneFencePurpose`: an
   * unrecognised or typo'd value matches no task and drops the key (fail-safe).
   * Equality alone would not be enough — it would let a typo'd stored purpose pair
   * with an identically-typo'd caller and hand the key over.
   *
   * **Write-edge validation lives at the `billing key_purpose_set` action seam**
   * (`packages/mcp/src/army/billing.ts` + `packages/db/src/queries/byok-purpose.ts`,
   * #1490). A designation with an unrecognised purpose is REFUSED at input with a
   * message naming the value and the admissible set, before any row is touched.
   * The READ-side validator stays because the column stays text + Zod-at-read —
   * the same fail-safe posture, the same reason a `CHECK` constraint was rejected
   * here (#1486): a constraint guards the database, not the seam a bad value
   * enters, and a stored unknown value must stay INERT (drop the key) rather than
   * throw on read and take the whole pool down over one row. The vocabulary is
   * still moving — `scout` and `plan` pass `isLaneFencePurpose` but are returned
   * by no code path today (admissible-but-dead). Freezing that in DDL is a
   * one-way door.
   *
   * NOT every lane is equally strong, and the verb surfaces this at the point of
   * choice — not only in this docblock — so an operator designating a key knows
   * which they picked:
   *
   * - `gardener` is the only lane no envelope can reach that a real caller still
   *   produces: `routePurposeForEnvelope` never returns it, and the janitor hands it
   *   in directly. A hard fence — as opposed to a DEAD one (`scout`/`plan`, below).
   * - `review` and `ci-triage` are ORIGIN-derived, so no task TEXT steers into them.
   *   But `origin` is caller-supplied on `POST /v1/tasks`, so a `tasks:write`
   *   principal can assert one: fenced against prose and against every in-cluster
   *   lane, NOT against an authenticated caller.
   * - `coding`/`quick`/`general`/`debugging-qa` are parsed out of the task's title or
   *   problem by `parseJobClassTag`, which on a public repo is text an external
   *   reporter writes. Those ORGANISE budget; they do not fence it.
   * - `scout` and `plan` are returned by NEITHER function, so — exactly like
   *   `default` — a key designated to one of them resolves for nothing today.
   *
   * `packages/db/src/schema.ts` carries the same caveat at the column.
   *
   * Distinct from {@link earmark}, which fences on the REPO's visibility (donated
   * inference may only sign public work). A key can carry both; the two AND.
   */
  purpose: z.string().min(1).nullish(),
  /**
   * The key's TAGGED MODEL CATALOGUE (`byok_key_models`, arc 101 slice 02) — the
   * models this credential can address and which lane tier each one serves. A key
   * is a credential; a model is a thing the credential can address, so "several
   * models at `smart`" is expressible here and is not on {@link model}.
   *
   * Loaded by `listPoolKeys` (the resolution read) and consumed by
   * `resolveLlmFromPool`, which answers a `(key, model)` PAIR: a tagged model
   * serving the task's tier is what the pair carries, and {@link model} is the
   * fallback when the key has no tag that serves it.
   *
   * Absent/empty = an untagged key — the pre-slice-02 behaviour, i.e. exactly
   * {@link model}. Every backfilled row is one untagged entry equal to
   * {@link model}, so a pool loaded after migration 0165 resolves identically to
   * one loaded before it.
   */
  models: z.array(BYOKKeyModelSchema).optional(),
  /**
   * Billing nature (arc 101 slice 03) — `metered` (pay-per-token) or `subscription`
   * (flat-rate coding plan). Derived at insert from the endpoint; the dashboard
   * shows it as a badge. Null/absent = a legacy row never classified (the resolver
   * falls back to URL inference), same nullish posture as the pool fields above.
   */
  kind: BYOKKeyKindSchema.nullish(),
  /**
   * The owner's DECLARED concurrency capacity (`byok_keys.capacity`, capacity-aware
   * scheduling) — how many model streams this key's plan sustains at once. The
   * ceiling admission holds the key to; telemetry may only pull it lower
   * (`effectiveKeyCapacity`). Null/absent = undeclared: the platform's default for the
   * key's kind + provider applies (`defaultKeyCapacity`). Nullish like the pool fields.
   */
  capacity: z.number().int().min(1).nullish(),
  verifiedAt: z.date().nullable(),
  invalidAt: z.date().nullable(),
  lastError: z.string().nullable(),
  /**
   * #1515: when the next verify attempt is owed, set on a TRANSIENT failure at
   * the verify seam (`packages/billing/src/byok.ts`). Cleared by `markKeyValid`
   * and retracted by `markKeyInvalid`. The assigner reads it to find keys due
   * for a re-probe; a stale stamp is the signal that a blip — a network fault,
   * a `5xx`, or a provider cooldown — is keeping the key out of the pool. See
   * {@link classifyVerifyFailure} for what counts as transient and
   * {@link verifyRetryDelayMs} for how far out the stamp lands.
   *
   * `.nullish()` rather than `.nullable()` so existing test fixtures in
   * `packages/agent` (which spread base BYOKKey shapes without this column) keep
   * typechecking — the column reads back as `null` in `normalizeKey` either way
   * (a missing DB value and a missing fixture entry resolve identically).
   */
  verificationRetryAt: z.date().nullish(),
  createdAt: z.date(),
});
export type BYOKKey = z.infer<typeof BYOKKeySchema>;
